import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, IconButton, InputAdornment } from "@mui/material";
import { useSnackbar } from "notistack";
import { Button, Input, AuthLayout, Link } from "../theme/components/index.js";
import { colorsFoundation } from "../theme/tokens/colors-foundation.js";
import { dimensions } from "../theme/tokens/dimensions.js";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import UserContext from "../contexts/UserContext.js";
import {
  requestLogin,
  requestPasswordReset,
  resetPassword,
} from "../services/dataService.js";
import { processLoginSuccess } from "../services/authService.js";
import {
  applyUserAfterLogin,
  resolveLoginMessage,
} from "../services/loginHelpers.js";
import TwoFactorFlow from "../components/auth/TwoFactorFlow.jsx";
import PasswordResetForm from "../components/auth/PasswordResetForm.jsx";
import EnvironmentBanner from "../components/EnvironmentBanner.jsx";

/**
 * Login Page - Schadenportal
 * Matches the Figma design (brand-agnostic; skinned via the active brand)
 */
export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const userContext = useContext(UserContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("login");
  const [twoFactorData, setTwoFactorData] = useState(null);
  const [secToken, setSecToken] = useState("");

  useEffect(() => {
    if (userContext?.user) {
      navigate("/home");
    }
  }, [userContext?.user, navigate]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get("token") || "";
    const action = searchParams.get("action");
    if (token) {
      setSecToken(token);
    }

    if (token) {
      setView("setNew");
    } else if (action === "reset-password") {
      setView("sendLink");
    }
  }, [location.search]);

  const showResponse = ({
    error = null,
    response = null,
    variant = "error",
    fallback = null,
  }) => {
    const message = resolveLoginMessage({ error, response, fallback });
    enqueueSnackbar(message, { variant });
    if (error) {
      console.error("[LOGIN] Error response", error);
    }
  };

  const handleSetUser = (user) => {
    applyUserAfterLogin({ userContext, user, navigate });
  };

  const processLoginData = async (data, fallbackMessage) => {
    try {
      const user = await processLoginSuccess(data);
      if (user) {
        handleSetUser(user);
        return true;
      }
      showResponse({ fallback: fallbackMessage });
    } catch (error) {
      showResponse({ error });
    }
    return false;
  };

  const handleTwoFactorSuccess = async (verificationData) => {
    try {
      return await processLoginData(
        verificationData,
        "Fehler beim Abrufen der Benutzerdaten nach der 2FA-Verifizierung."
      );
    } finally {
      setLoading(false);
      setView("login");
      setTwoFactorData(null);
      setPassword("");
    }
  };

  const handleTwoFactorCancel = () => {
    setView("login");
    setTwoFactorData(null);
    setPassword("");
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    console.log("[LOGIN] Attempting login", { email });

    await requestLogin(email, password, {
      setIsLoading: setLoading,
      onSuccess: async (data) => {
        await processLoginData(
          data,
          "Fehler beim Abrufen der Benutzerdaten nach dem Login."
        );
      },
      onError: (error) => {
        showResponse({ error });
      },
      onTwoFactorRequired: (data) => {
        setTwoFactorData(data);
        setView("twoFactor");
        showResponse({
          response: { data },
          variant: "info",
          fallback: "Zwei-Faktor-Authentifizierung erforderlich.",
        });
      },
    });
  };

  const handleForgotPassword = () => {
    setView("sendLink");
    setTwoFactorData(null);
  };

  const handleBackToLogin = () => {
    setView("login");
    setTwoFactorData(null);
    setLoading(false);
  };

  const handleSendResetRequest = async () => {
    if (!email) {
      showResponse({ fallback: "Bitte geben Sie Ihre E-Mail-Adresse ein." });
      return;
    }

    await requestPasswordReset(email, {
      setIsLoading: setLoading,
      onSuccess: (response) => {
        enqueueSnackbar("Kontrollieren Sie Ihren Posteingang.", {
          variant: "success",
        });
        setView("afterResetRequest");
      },
      onError: (error) => {
        showResponse({ error });
      },
    });
  };

  const handleResetSubmit = async (
    { token, password: newPassword },
    formMethods
  ) => {
    let success = false;
    await resetPassword(token, newPassword, {
      setIsLoading: setLoading,
      setError: formMethods?.setError,
      enqueueSnackbar,
      successMessage: "Passwort wurde erfolgreich gesetzt.",
      onSuccess: async (response) => {
        if (response.status === 200) {
          const loginData = response.data?.data;
          const loginSuccess = await processLoginData(
            loginData,
            "Fehler beim Abrufen der Benutzerdaten nach dem Login."
          );
          success = Boolean(loginSuccess);
        } else {
          showResponse({
            response,
            fallback: "Fehler beim Zurücksetzen des Passworts.",
          });
        }
      },
      onError: (error) => {
        showResponse({ error });
      },
    });

    if (success) {
      setView("login");
      setPassword("");
      setSecToken("");
    }

    return success;
  };

  const renderLoginForm = () => (
    <Box
      component="form"
      onSubmit={handleLogin}
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: dimensions["2xl"],
      }}
    >
      <Input
        label="E-Mail-Adresse"
        placeholder="name@beispiel.de"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        fullWidth
        autoComplete="username"
      />

      <Input
        label="Passwort"
        placeholder="Ihr Passwort"
        type={showPassword ? "text" : "password"}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        fullWidth
        autoComplete="current-password"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={
                  showPassword ? "Passwort verbergen" : "Passwort anzeigen"
                }
                onClick={() => setShowPassword((show) => !show)}
                edge="end"
                size="small"
              >
                {showPassword ? (
                  <VisibilityOffIcon fontSize="small" />
                ) : (
                  <VisibilityIcon fontSize="small" />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ textAlign: "right", mt: `-${dimensions.lg}` }}>
        <Link variant="primary" onClick={handleForgotPassword} underline="hover">
          Passwort vergessen?
        </Link>
      </Box>

      <Button
        variant="primary"
        type="submit"
        size="large"
        fullWidth
        disabled={loading}
        endIcon={!loading && <ArrowForwardIcon fontSize="small" />}
      >
        {loading ? "Wird geladen..." : "Anmelden"}
      </Button>
    </Box>
  );

  const renderSendLink = () => (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: dimensions["2xl"] }}
    >
      <Box
        sx={{
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          gap: dimensions["md"],
        }}
      >
        <Typography variant="h6">Passwort zurücksetzen</Typography>
        <Typography color="text.secondary">
          Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum
          Zurücksetzen Ihres Passworts.
        </Typography>
      </Box>

      <Input
        label="E-Mail-Adresse"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        fullWidth
        autoComplete="username"
      />

      <Box
        sx={{ display: "flex", flexDirection: "column", gap: dimensions["lg"] }}
      >
        <Button
          variant="primary"
          size="large"
          onClick={handleSendResetRequest}
          disabled={loading}
        >
          {loading ? "Wird gesendet..." : "Zurücksetzlink senden"}
        </Button>
        <Button variant="tertiary" onClick={handleBackToLogin}>
          Zurück zur Anmeldung
        </Button>
      </Box>
    </Box>
  );

  const renderAfterResetRequest = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: dimensions["2xl"],
        textAlign: "center",
      }}
    >
      <Typography>
        Wir haben Ihnen eine E-Mail mit einem Link zum Zurücksetzen Ihres
        Passworts gesendet. Bitte überprüfen Sie Ihren Posteingang und folgen
        Sie den Anweisungen.
      </Typography>
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: dimensions["lg"] }}
      >
        <Button variant="primary" onClick={handleBackToLogin}>
          Zur Anmeldung
        </Button>
        <Button variant="tertiary" onClick={() => setView("sendLink")}>
          Neuen Link anfordern
        </Button>
      </Box>
    </Box>
  );

  const renderSetNewPassword = () => (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: dimensions["2xl"] }}
    >
      <Typography variant="h6" align="center">
        Neues Passwort festlegen
      </Typography>
      <PasswordResetForm
        token={secToken}
        onTokenChange={setSecToken}
        onSubmit={async (data, formMethods) =>
          handleResetSubmit(data, formMethods)
        }
        isLoading={loading}
      />
      <Button variant="tertiary" onClick={handleBackToLogin}>
        Zurück zur Anmeldung
      </Button>
    </Box>
  );

  const renderContent = () => {
    if (view === "twoFactor" && twoFactorData) {
      return (
        <TwoFactorFlow
          email={email}
          challengeToken={twoFactorData.challenge_token}
          onSuccess={handleTwoFactorSuccess}
          onCancel={handleTwoFactorCancel}
          initialChallengeData={twoFactorData.challenge_data}
        />
      );
    }

    switch (view) {
      case "sendLink":
        return renderSendLink();
      case "afterResetRequest":
        return renderAfterResetRequest();
      case "setNew":
        return renderSetNewPassword();
      case "login":
      default:
        return renderLoginForm();
    }
  };

  const isLoginView = view === "login";

  return (
    <AuthLayout topSlot={<EnvironmentBanner />}>
      {isLoginView && (
        <Box sx={{ mb: dimensions["3xl"] }}>
          <Typography
            component="h1"
            sx={{
              fontSize: "28px",
              fontWeight: 700,
              letterSpacing: "-0.5px",
              color: colorsFoundation.neutral.softblack,
              mb: dimensions.md,
            }}
          >
            Willkommen zurück
          </Typography>
          <Typography
            sx={{
              color: colorsFoundation.gray[500],
              fontSize: "15px",
              lineHeight: 1.55,
            }}
          >
            Melden Sie sich mit Ihren Zugangsdaten an.
          </Typography>
        </Box>
      )}

      {renderContent()}
    </AuthLayout>
  );
}
